import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from './useAuth';

export function usePolicies() {
  const { user } = useAuth();
  const [myPolicies, setMyPolicies] = useState([]);
  const [sharedPolicies, setSharedPolicies] = useState([]);
  const [accountLinks, setAccountLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Refs to hold current link state (prevents stale closures in dual listeners)
  const linksAsARef = useRef([]);
  const linksAsBRef = useRef([]);
  const mergeTimeoutRef = useRef(null);

  // Merge and dedupe links from both queries (debounced to prevent double updates)
  const mergeLinks = useCallback(() => {
    // Debounce: only merge after 50ms of no changes
    if (mergeTimeoutRef.current) {
      clearTimeout(mergeTimeoutRef.current);
    }
    mergeTimeoutRef.current = setTimeout(() => {
      const allLinks = [...linksAsARef.current, ...linksAsBRef.current];
      const uniqueLinks = Array.from(
        new Map(allLinks.map((link) => [link.id, link])).values()
      );
      setAccountLinks(uniqueLinks);
    }, 50);
  }, []);

  // Listen to account links for sharing (using userA/userB queries)
  useEffect(() => {
    if (!user?.uid) {
      linksAsARef.current = [];
      linksAsBRef.current = [];
      setAccountLinks([]);
      return;
    }

    const queryA = query(
      collection(db, 'accountLinks'),
      where('userA', '==', user.uid),
      where('status', '==', 'active')
    );

    const queryB = query(
      collection(db, 'accountLinks'),
      where('userB', '==', user.uid),
      where('status', '==', 'active')
    );

    const unsubscribeA = onSnapshot(
      queryA,
      (snapshot) => {
        linksAsARef.current = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        mergeLinks();
      },
      (err) => console.error('🔥 [usePolicies] accountLinks-A listener error:', err.message)
    );

    const unsubscribeB = onSnapshot(
      queryB,
      (snapshot) => {
        linksAsBRef.current = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        mergeLinks();
      },
      (err) => console.error('🔥 [usePolicies] accountLinks-B listener error:', err.message)
    );

    return () => {
      unsubscribeA();
      unsubscribeB();
      if (mergeTimeoutRef.current) {
        clearTimeout(mergeTimeoutRef.current);
      }
    };
  }, [user?.uid, mergeLinks]);

  // Fetch shared policies from linked accounts
  // Query by viewableBy array-contains to match security rules
  const fetchSharedPolicies = useCallback(async () => {
    if (!user?.uid || accountLinks.length === 0) {
      setSharedPolicies([]);
      return;
    }

    try {
      // Query policies where I'm in viewableBy (matches security rule)
      const policiesQuery = query(
        collection(db, 'policies'),
        where('viewableBy', 'array-contains', user.uid),
        orderBy('category'),
        orderBy('name')
      );
      const snapshot = await getDocs(policiesQuery);

      // Build owner info from accountLinks for display
      const linkedUserInfo = {};
      for (const link of accountLinks) {
        const isUserA = link.userA === user.uid;
        const linkedUserId = isUserA ? link.userB : link.userA;
        linkedUserInfo[linkedUserId] = {
          displayName: isUserA ? link.userBDisplayName : link.userADisplayName,
          email: isUserA ? link.userBEmail : link.userAEmail
        };
      }

      const shared = snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((policy) => policy.excludeFromSharing !== true && !policy.archived)
        .map((policy) => {
          const ownerInfo = linkedUserInfo[policy.userId] || {};
          return {
            ...policy,
            isShared: true,
            owner: ownerInfo.displayName || ownerInfo.email?.split('@')[0] || 'Shared',
            ownerDisplayName: ownerInfo.displayName,
            ownerEmail: ownerInfo.email
          };
        });

      setSharedPolicies(shared);
    } catch (error) {
      console.error('Error fetching shared policies:', error);
      setSharedPolicies([]);
    }
  }, [user?.uid, accountLinks]);

  // Refetch shared policies when account links change
  useEffect(() => {
    fetchSharedPolicies();
  }, [fetchSharedPolicies]);

  useEffect(() => {
    if (!user?.uid) {
      setMyPolicies([]);
      setLoading(false);
      return;
    }

    // Query policies by userId (multi-tenant)
    const q = query(
      collection(db, 'policies'),
      where('userId', '==', user.uid),
      orderBy('category'),
      orderBy('name')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const policiesData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        setMyPolicies(policiesData);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching policies:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.uid]);

  // Combine my policies with shared policies
  const allPolicies = useMemo(() => {
    return [...myPolicies, ...sharedPolicies];
  }, [myPolicies, sharedPolicies]);

  const addPolicy = async (policyData) => {
    if (!user?.uid) {
      throw new Error('User must be logged in to add policies');
    }

    try {
      await addDoc(collection(db, 'policies'), {
        ...policyData,
        userId: user.uid, // Multi-tenant: owner by Firebase UID
        createdAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error adding policy:', err);
      throw err;
    }
  };

  const updatePolicy = async (id, policyData) => {
    try {
      const policyRef = doc(db, 'policies', id);
      await updateDoc(policyRef, policyData);
    } catch (err) {
      console.error('Error updating policy:', err);
      throw err;
    }
  };

  const deletePolicy = async (id) => {
    try {
      await deleteDoc(doc(db, 'policies', id));
    } catch (err) {
      console.error('Error deleting policy:', err);
      throw err;
    }
  };

  const archivePolicy = async (id) => {
    try {
      const policyRef = doc(db, 'policies', id);
      await updateDoc(policyRef, { archived: true });
    } catch (err) {
      console.error('Error archiving policy:', err);
      throw err;
    }
  };

  const unarchivePolicy = async (id) => {
    try {
      const policyRef = doc(db, 'policies', id);
      await updateDoc(policyRef, { archived: false });
    } catch (err) {
      console.error('Error unarchiving policy:', err);
      throw err;
    }
  };

  // Check if current user can edit a policy
  const canEdit = (policy) => {
    return policy?.userId === user?.uid;
  };

  // Get owner display text for shared policies
  const getOwnerDisplayText = (policy) => {
    if (!policy?.isShared) return null;
    return policy.ownerDisplayName || policy.ownerEmail || 'Shared';
  };

  // Separate active and archived policies (only show my archived)
  const activePolicies = allPolicies.filter(p => !p.archived);
  const archivedPolicies = myPolicies.filter(p => p.archived);

  return {
    policies: activePolicies,
    myPolicies,
    sharedPolicies,
    archivedPolicies,
    loading,
    error,
    addPolicy,
    updatePolicy,
    deletePolicy,
    archivePolicy,
    unarchivePolicy,
    canEdit,
    getOwnerDisplayText
  };
}
